<script setup lang="ts">
import type { Ui2DropdownMenuItem } from '@mop/ui2/types';
import SVGClose from '@mop/shared/images/misc/header-close.svg?component';
import SVGMopLogo from '@mop/shared/images/logo/logo_mop.svg?component';
import SVGMopLogoSignet from '@mop/shared/images/logo/logo_mop_signet.svg?component';
import SVGSearch from '@mop/shared/images/misc/search.svg?component';
import SVGBurger from '@mop/shared/images/misc/burger.svg?component';
import SVGHeart from '@mop/shared/images/misc/header-heart.svg?component';
import type { NavigationModel } from '@/types/navigationModel';

defineOptions({
  name: 'MopHeader',
});

const { $gtm, $breakpoint, $mopI18n, $mopConfig } = useNuxtApp();
const headerOverlay = useMopOverlay();
const { activePathRef } = useMopNavigation();
const { wishlistItemsRef, init: initWishlist } = useMopWishlist();
const { getAlternates } = useMopSeo();

const isSearchSuggestionOpenRef = ref(false);
const isMobileMenuOpenRef = ref(false);
const componentNames = {
  mobileMenu: 'MopHeaderMobileMenuOverlay',
  menu: 'MopHeaderMenuOverlay',
  search: 'MopHeaderSearchSuggestionsOverlay',
};
const lastOpenedCategoryRef: Ref<NavigationModel | undefined> = ref();
let closeTimer: any;

const localeItems: Ui2DropdownMenuItem[] = $mopConfig
  .getAvailableLocales()
  .filter((locale) => locale !== $mopI18n.locale)
  .map((locale) => {
    return {
      text: $mopI18n.t(`locale.lang.${locale}`),
      callbackFn: () => handleLanguageClickItem(locale),
    };
  });

function redirect(newLocale: string) {
  const alternate = getAlternates()?.find((alternate) => alternate.lang === newLocale);
  const win: Window = window;
  win.location = `/${newLocale}${alternate?.href}` || `/${newLocale}`;
}

function handleLanguageClickItem(newLocale: string) {
  $gtm.trackEvent(
    {
      eventName: 'language_switch',
      parameters: ['click', $mopI18n.locale, newLocale],
    },
    true,
  );

  setTimeout(() => {
    redirect(newLocale);
  }, 100);
}

onMounted(initWishlist);

watch(activePathRef, resetLastOpenedCategory);
watch($breakpoint.currentRef, (viewport) => {
  if (headerOverlay?.activeOverlayRef?.value?.componentName === componentNames.menu && ['xs', 's'].includes(viewport)) {
    resetLastOpenedCategory();
    headerOverlay.closeAll();
  } else if (
    headerOverlay?.activeOverlayRef?.value?.componentName === componentNames.mobileMenu &&
    !['xs', 's'].includes(viewport)
  ) {
    headerOverlay.closeAll();
  }
});

async function handleSearchClick() {
  if (isSearchSuggestionOpenRef.value) {
    return await headerOverlay.closeAll();
  }
  isSearchSuggestionOpenRef.value = true;

  await headerOverlay.open({
    type: 'top',
    componentName: componentNames.search,
    overrideParams: {
      showClose: false,
      forceRender: true,
      lockScroll: '.search-suggest__scroll',
    },
    onClose() {
      isSearchSuggestionOpenRef.value = false;
    },
  });

  $gtm.trackEvent({
    eventName: 'site_search_start',
    parameters: [$gtm.getCurrentPageType()],
  });
}

function trackJobButtonClick() {
  $gtm.trackEvent({
    eventName: 'our_jobs_button',
    parameters: ['click', document.location.pathname],
  });
}

async function handleMobileMenuClick() {
  isMobileMenuOpenRef.value = !isMobileMenuOpenRef.value;

  if (!isMobileMenuOpenRef.value) {
    return await headerOverlay.closeAll();
  }

  await headerOverlay.open({
    componentName: componentNames?.mobileMenu,
    type: 'left',
    overrideParams: {
      forceRender: true,
      lockScroll: '.mobile-menu',
    },
    onClose() {
      isMobileMenuOpenRef.value = false;
    },
  });
}

function resetLastOpenedCategory() {
  lastOpenedCategoryRef.value = undefined;
}

function handleMouseLeave() {
  if (headerOverlay?.activeOverlayRef?.value?.isOpen) {
    return;
  }
  resetLastOpenedCategory();
}

async function handleMouseOver(category: NavigationModel) {
  clearTimeout(closeTimer);
  if (lastOpenedCategoryRef.value?.getUid() === category.getUid()) {
    return;
  }

  if (!category.hasChildren()) {
    return await headerOverlay.closeAll();
  }

  closeTimer = setTimeout(() => {
    headerOverlay.open({
      componentName: componentNames.menu,
      type: 'left',
      overrideParams: {
        forceRender: true,
        shadowColor: 'dark',
        mouseLeaveTimeout: 2000,
        closeOnMouseLeave: true,
      },
      props: {
        category,
      },
      onOpen() {
        lastOpenedCategoryRef.value = category;
      },
      onClose() {
        resetLastOpenedCategory();
      },
    });
  }, 150);
}
</script>

<template>
  <header
    :class="[
      'header',
      {
        'header--inactive': isSearchSuggestionOpenRef,
      },
    ]"
  >
    <div class="header__mobile-left mobile-only">
      <button class="header__mobile-navbar-button header__icon" @click="handleMobileMenuClick">
        <SVGClose v-show="isMobileMenuOpenRef" class="header__icon-close-svg" width="30" height="19" />
        <SVGBurger v-show="!isMobileMenuOpenRef" width="30" height="19" />
      </button>

      <button class="header__icon header__icon-search-mobile" @click="handleSearchClick">
        <SVGClose v-show="isSearchSuggestionOpenRef" width="30" height="19" />
        <SVGSearch v-show="!isSearchSuggestionOpenRef" width="30" height="19" />
      </button>
    </div>

    <NuxtLink class="header__small-logo desktop-only" :to="$mopI18n.localePath('')" no-prefetch>
      <SVGMopLogoSignet class="header__breadcrumb-logo-svg" width="40" height="40" />
    </NuxtLink>

    <nav
      :class="[
        'header__nav desktop-only',
        {
          'header__nav--active': lastOpenedCategoryRef !== undefined,
        },
      ]"
      @mouseleave="handleMouseLeave"
    >
      <div v-for="item in $navigationList" :key="item.getUid()">
        <UiNuxtLink
          v-bind="item.getLink()"
          :class="[
            'header__link',
            {
              'header__link--hover': lastOpenedCategoryRef && lastOpenedCategoryRef.getUid() === item.getUid(),
              'header__link--active':
                item.getLink().isLink && activePathRef.length > 0 ? activePathRef[0].getUid() === item.getUid() : false,
            },
          ]"
          @mouseenter="() => handleMouseOver(item)"
        >
          {{ item.getName() }}
        </UiNuxtLink>
      </div>
    </nav>
    <div class="header__logo lock-scroll-padding">
      <NuxtLink
        :title="$mopI18n.t('locale.brandName')"
        class="header__logo-link"
        :to="$mopI18n.localePath('')"
        no-prefetch
      >
        <SVGMopLogo class="header__logo-image header__filled-svg" width="190" height="40" />
      </NuxtLink>
    </div>
    <div class="header__actions">
      <Ui2DropdownMenu :items="localeItems" has-icon-rotation class="header__actions-language-switch">
        <template #cta>
          <Ui2Button
            class="mobile-only header__actions-lang"
            size="sm"
            type="outline"
            :label="$mopI18n.locale"
            icon-end="chevron-down"
          />
          <Ui2Button
            class="desktop-only"
            size="sm"
            type="outline"
            :label="$mopI18n.t(`locale.lang.${$mopI18n.locale}`)"
            icon-end="chevron-down"
          />
        </template>
      </Ui2DropdownMenu>
      <NuxtLink
        class="header__actions-job-search"
        :to="$mopI18n.localePath(`${$urls.CAREER_PAGE}`)"
        :style="{ '--bg-color': $mopConfig.getHeaderJobButtonColor() }"
        no-prefetch
        @click="trackJobButtonClick"
      >
        {{ $mopI18n.t('locale.jobs.search_button') }}
      </NuxtLink>
      <button class="header__icon header__icon--search" @click="handleSearchClick">
        <SVGClose v-show="isSearchSuggestionOpenRef" width="30" height="19" />
        <SVGSearch v-show="!isSearchSuggestionOpenRef" width="30" height="19" />
      </button>
      <NuxtLink
        class="header__icon header__icon--wishlist"
        :to="$mopI18n.localePath(`${$urls.WISHLIST_PAGE}`)"
        no-prefetch
      >
        <SVGHeart width="30" height="19" />
        <span class="header__wishlist-count">
          {{ wishlistItemsRef.length || '' }}
        </span>
      </NuxtLink>
    </div>
  </header>
</template>

<style lang="scss" scoped>
header {
  @include z(layers, above-layer);

  position: sticky;
  top: 0;
  width: 100%;
  display: flex;
  background: $white;
  color: $black;
  align-items: center;
  justify-content: space-between;
  height: $header-height;
  padding: 0 $global-padding;

  @include apply-upto(small) {
    height: $mobile-header-height;
  }

  @include apply-only-touch-device {
    height: $mobile-header-height;
  }
}

.header__nav {
  display: flex;
}

.header__small-logo {
  display: none;
}

.header__link {
  @include link-not-underlined(0);
  @include text-style(strong);

  text-transform: uppercase;
  margin-right: $space15;
}

.header__icon {
  @include link-neutral($space10);

  border: 0;
  line-height: 0;
  background: transparent;
  cursor: pointer;
  color: inherit;
  width: $header-icon-width;
  overflow: hidden;
  margin-right: -10px;

  &::after {
    color: inherit;
  }
}

.header__mobile-navbar-button {
  margin-left: -6px;
}

.header__logo {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
}

.header__logo-image {
  @include apply-upto(small) {
    height: 26px;
    width: auto;
  }

  @include apply-only-touch-device {
    height: 26px;
    width: auto;
  }
}

.desktop-only {
  @include apply-upto(small) {
    display: none;
  }

  @include apply-only-touch-device {
    display: none;
  }
}

.mobile-only {
  @include apply-from(medium) {
    display: none;
  }

  @include apply-only-touch-device {
    display: initial;

    &.header__actions-lang {
      display: flex;
    }
  }
}

.header__nav--active {
  .header__link {
    opacity: $disabled-opacity;

    &::after {
      border: 0;
    }
  }

  .header__link--hover {
    opacity: 1;
  }
}

.header--inactive {
  .header__link {
    opacity: $disabled-opacity;
  }
}

// exceptions

@media (min-width: 768px) and (max-width: 1100px) and (hover: hover) and (pointer: fine) {
  header {
    justify-content: flex-start;

    .header__small-logo {
      display: inline-flex;
    }

    .header__nav {
      flex-grow: 1;
      padding-left: $space20;
    }

    .header__logo {
      display: none;
    }
  }
}

.header__actions {
  display: flex;
  align-items: center;
}

.header__actions-language-switch {
  margin-right: $space-12;

  :deep(.ui-button) {
    height: 34px;
    border-radius: 0;

    @include apply-upto(medium) {
      padding-left: $space-8;
      padding-right: $space-8;
      gap: $space-4;
    }

    @include apply-upto(tiny) {
      padding-left: $space-4;
      padding-right: $space-2;
      gap: $space-2;
    }
  }

  :deep(.ui-dropdown-menu__options-wrapper) {
    width: 200px;
  }

  @include apply-upto(small) {
    margin-right: 0;
  }
}

.header__actions-job-search {
  @include text-style(strong);

  background: var(--bg-color, $black);
  color: $white;
  text-transform: uppercase;
  padding: 8px $space15;
  margin-right: $space-12;
  align-self: center;

  @include apply-upto(small) {
    display: none;
  }

  @include apply-only-touch-device {
    display: none;
  }
}

.header__icon--wishlist {
  margin-left: $space10;
  margin-right: -$space5;
  position: relative;

  @include apply-upto(small) {
    margin-left: $space5;
  }
}

.header__icon--search {
  @include apply-upto(small) {
    display: none;
  }
}

.header__wishlist-count {
  position: absolute;
  display: block;
  top: 4px;
  right: 5px;
  height: 15px;
  font-size: 10px;
  padding-left: $space5;
  text-decoration: none;
}

.header__mobile-left {
  @include apply-upto(small) {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $space-8;
  }
}

.header__icon-search-mobile {
  display: none;
  margin-left: $space-4;

  @include apply-upto(small) {
    display: block;
  }
}
</style>
